import { useEffect, useMemo, useState } from "react";
import { onAuthStateChanged, onIdTokenChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../lib/firebase";

function normalizeCurrencyCode(value) {
  const code = String(value || "USD").trim().toUpperCase();
  return code === "BGN" ? "EUR" : code || "USD";
}

/**
 * Returns:
 *  - loading: boolean
 *  - role: string ("free" if unknown)
 *  - currency: string | undefined (from users/{uid}.currency)
 *  - gates: {
 *      nav?: { metal?: boolean, truck?: boolean }
 *      details?: { weight?: boolean }
 *      features: { dateFilter: boolean }  // <-- use this in FiltersPanel
 *    }
 */
export function useRolePermissions() {
  const [loading, setLoading] = useState(true);
  const [uid, setUid] = useState(null);
  const [role, setRole] = useState("free");
  const [currency, setCurrency] = useState("USD");
  const [permDoc, setPermDoc] = useState(null); // raw permission document (optional)

  // Watch auth user and token changes (reflect custom-claim updates live)
  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      setUid(user ? user.uid : null);
    });
    
    // Force re-fetch when token changes (custom claims updated)
    let lastRole = null;
    const unsubTok = onIdTokenChanged(auth, async (user) => {
      if (!user) return;
      try {
        const token = await user.getIdTokenResult();
        const claims = token?.claims || {};
        const currentRole = claims.role || 
          (claims.admin ? "admin" :
          claims.managerPlus ? "managerPlus" :
          claims.manager ? "manager" : "free");
        
        // Only trigger re-fetch if role actually changed
        if (currentRole !== lastRole) {
          lastRole = currentRole;
          setUid((u) => u); // Force effect to re-run
        }
      } catch {}
    });
    
    return () => {
      unsubAuth();
      unsubTok();
    };
  }, []);

  // Fetch user profile (users/{uid}) and role permissions (permissions/{role})
  useEffect(() => {
    let isMounted = true;
    async function run() {
      setLoading(true);
      try {
        let resolvedRole = "free";
        let resolvedCurrency = "USD";

        if (uid) {
          // users/{uid} holds "discount" (role name) and "currency"
          const userSnap = await getDoc(doc(db, "users", uid));
          if (userSnap.exists()) {
            const data = userSnap.data() || {};
            if (typeof data.discount === "string" && data.discount.trim()) {
              resolvedRole = data.discount.trim();
            }
            if (typeof data.currency === "string" && data.currency.trim()) {
              resolvedCurrency = normalizeCurrencyCode(data.currency);
            }
          }

          // Elevate to privileged base role from custom claims when present
          try {
            const cu = auth.currentUser;
            if (cu) {
              const token = await cu.getIdTokenResult();
              const cl = token?.claims || {};
              if (cl.admin) resolvedRole = "admin";
              else if (cl.managerPlus) resolvedRole = "managerPlus";
              else if (cl.manager) resolvedRole = "manager";
            }
          } catch {
            // ignore
          }
        }

        // Normalize role to lowercase token
        const roleKey = resolvedRole.toLowerCase();

        // Try to load the matching permissions/{role} doc (optional)
        let perm = null;
        try {
          const pSnap = await getDoc(doc(db, "permissions", roleKey));
          if (pSnap.exists()) {
            perm = pSnap.data();
          }
        } catch {
          // ignore; gates will fall back to defaults
        }

        if (!isMounted) return;
        setRole(resolvedRole);
        setCurrency(resolvedCurrency);
        setPermDoc(perm);
      } catch (e) {
        if (!isMounted) return;
        // On any failure, default to free
        setRole("free");
        setPermDoc(null);
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    run();
    return () => {
      isMounted = false;
    };
  }, [uid]);

  // Build gates from the permission doc (if present) and role
  const gates = useMemo(() => {
    const roleKey = (role || "free").toLowerCase();

    // pull optional fields from permissions doc if your structure provides them
    const nav = {
      metal: !!permDoc?.navigation?.metals || !!permDoc?.navigation?.metal,
      truck: !!permDoc?.navigation?.trucks || !!permDoc?.navigation?.truck,
    };

    const details = {
      weight: !!permDoc?.details?.weight,
    };

    // Feature gates — central place to decide visibility
    const canSeeDateFilter = roleKey === "admin";

    return {
      nav,
      details,
      features: {
        dateFilter: canSeeDateFilter,
      },
    };
  }, [role, permDoc]);

  // Memoize the return value to prevent creating new object references
  return useMemo(
    () => ({ loading, role, currency, gates, uid }),
    [loading, role, currency, gates, uid]
  );
}
